import { FC, useContext, useEffect, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { useApi } from "../../../../http/api";
import { CustomError } from "../../../../http/errors";
import {
  AdminCommonSteelVersion,
  UpdateCommonSteelVersionNewImageBody,
  UpdateCommonSteelVersionRemoveImageBody,
} from "../../../../http/types/commonSteelVersions";
import { AdminGlobalSteelVersion } from "../../../../http/types/globalSteelVersions";
import { useSteelVersionsApi } from "../../../../http/useSteelVersions";
import FlexContent from "../../../ui/FlexContent";
import Header, { HeaderPath } from "../../../ui/Header";
import SteelVersionDetails, { UpsertSteelVersionForm } from "./SteelVersionDetails";

type CommonSteelVersionProps = {
  updateSteelVersion: (
    steelVersionId: string,
    body: UpdateCommonSteelVersionRemoveImageBody | UpdateCommonSteelVersionNewImageBody,
  ) => Promise<void>;
  trigger: () => void;
  data: AdminCommonSteelVersion | AdminCommonSteelVersion | AdminGlobalSteelVersion | undefined;
  error: CustomError | undefined;
  path?: HeaderPath | HeaderPath[];
  steelVersionId: string | undefined;
  title?: string;
  isGlobalSettings: boolean;
};

const CommonSteelVersion: FC<CommonSteelVersionProps> = ({
  updateSteelVersion,
  trigger,
  data,
  error,
  path,
  steelVersionId,
  title,
  isGlobalSettings,
}) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const api = useApi();
  const steelVersionsApi = useSteelVersionsApi();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (body: UpdateCommonSteelVersionNewImageBody | UpdateCommonSteelVersionRemoveImageBody) => {
    try {
      await updateSteelVersion(steelVersionId!, body);
      trigger();
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (formData: UpsertSteelVersionForm, newImage: File | undefined, removeImage: boolean) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name!,
      nameIt: formData.nameIt ?? null,
      nameDe: formData.nameDe ?? null,
      nameFr: formData.nameFr ?? null,
      nameEs: formData.nameEs ?? null,
      description: formData.description!,
      descriptionIt: formData.descriptionIt ?? null,
      descriptionDe: formData.descriptionDe ?? null,
      descriptionFr: formData.descriptionFr ?? null,
      descriptionEs: formData.descriptionEs ?? null,
      image: newImage?.name ?? undefined,
      order: formData.order!,
      visibility: formData.visibility,
    };
    if (newImage) {
      try {
        const signedUrl = await steelVersionsApi.generateUploadImageUrl(steelVersionId!, newImage.name, newImage.type);
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateCommonSteelVersionNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateCommonSteelVersionRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  useEffect(() => {
    steelVersionId && trigger();
  }, [steelVersionId]);

  return (
    <>
      {title && <Header title={title} error={error} path={path} />}
      <FlexContent padding direction="row">
        <SteelVersionDetails
          steelVersion={data}
          isGlobalSettings={isGlobalSettings}
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
        ></SteelVersionDetails>
      </FlexContent>
    </>
  );
};

export default CommonSteelVersion;
